import { memo, useEffect } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import { useNotifications } from '../../../hooks/useNotificationLog';
import {
  SEVERITY_COLORS,
  clearNotifications,
  markAllRead,
} from '../../../utils/notificationLog';
import { FONT_FAMILY } from '../../../constants/typography';

// Wall-clock, not "2m ago". A relative stamp needs a ticking clock, which is the
// same 1 Hz whole-tree re-render already hurting this app via useClockManager —
// and when an operator is reconstructing what happened at 68', the time on the
// wall is the useful number anyway. Formatted here rather than at push time so
// it costs nothing while the tray is shut.
const formatTime = (at) =>
  new Date(at).toLocaleTimeString('en-GB', { hour12: false });

// Memoised because the snapshot array gets a fresh identity on every push, so
// without it each new message re-renders all 50 rows instead of just the head.
const NotificationRow = memo(({ entry }) => (
  <Stack
    direction="row"
    alignItems="flex-start"
    spacing={1.25}
    sx={{ px: 2, py: 1, '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' } }}
  >
    <Box
      sx={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        mt: 0.65,
        flexShrink: 0,
        bgcolor: SEVERITY_COLORS[entry.severity],
      }}
    />
    <Typography
      sx={{
        flex: 1,
        fontFamily: FONT_FAMILY,
        fontSize: 14,
        lineHeight: 1.35,
        color: 'rgba(255,255,255,0.85)',
        wordBreak: 'break-word',
      }}
    >
      {entry.message}
    </Typography>
    {entry.count > 1 && (
      <Box
        sx={{
          flexShrink: 0,
          mt: 0.15,
          px: 0.65,
          py: 0.1,
          borderRadius: 1,
          border: '1px solid rgba(255,255,255,0.18)',
          color: 'rgba(255,255,255,0.6)',
          fontFamily: FONT_FAMILY,
          fontSize: 11,
          fontWeight: 700,
        }}
      >
        {`×${entry.count}`}
      </Box>
    )}
    <Typography
      sx={{
        flexShrink: 0,
        mt: 0.15,
        fontFamily: FONT_FAMILY,
        fontSize: 12,
        color: 'rgba(255,255,255,0.35)',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {formatTime(entry.at)}
    </Typography>
  </Stack>
));

NotificationRow.displayName = 'NotificationRow';

NotificationRow.propTypes = {
  entry: PropTypes.shape({
    message: PropTypes.string,
    severity: PropTypes.oneOf(['success', 'info', 'warning', 'error']),
    at: PropTypes.number,
    count: PropTypes.number,
  }).isRequired,
};

// Split out so the store subscription lives BELOW the Popover. MUI's Modal
// renders no children while closed, so this component — and therefore
// useNotifications() — only exists while the operator has the tray open. Keeping
// the subscription in the outer component would re-render it on every push even
// when shut, which is exactly the cost this design is meant to avoid.
const NotificationList = () => {
  const entries = useNotifications();

  // Opening the tray is reading it, so the badge clears immediately, and
  // anything arriving while it is open is read on arrival. This cannot loop:
  // markAllRead early-returns once the unread count is zero.
  useEffect(() => {
    markAllRead();
  }, [entries]);

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2, py: 1.25 }}
      >
        <Typography
          sx={{
            fontFamily: FONT_FAMILY,
            fontSize: 11,
            fontWeight: 600,
            color: 'rgba(255,255,255,0.35)',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
          }}
        >
          Notifications
        </Typography>
        {entries.length > 0 && (
          <Box
            component="button"
            onClick={clearNotifications}
            sx={{
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              p: 0,
              fontFamily: FONT_FAMILY,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.35)',
              transition: 'color 0.2s ease',
              '&:hover': { color: '#FEEC67' },
            }}
          >
            Clear
          </Box>
        )}
      </Stack>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

      {entries.length === 0 ? (
        <Box sx={{ px: 2, py: 3 }}>
          <Typography
            sx={{
              fontFamily: FONT_FAMILY,
              fontSize: 13,
              color: 'rgba(255,255,255,0.35)',
              textAlign: 'center',
            }}
          >
            No notifications
          </Typography>
        </Box>
      ) : (
        <Box sx={{ maxHeight: 360, overflowY: 'auto', py: 0.5 }}>
          {entries.map((entry) => (
            <NotificationRow key={entry.id} entry={entry} />
          ))}
        </Box>
      )}
    </>
  );
};

const NotificationPanel = ({ open, anchorEl, onClose }) => {
  // The Popover root is pointer-transparent (see slotProps below), so MUI's own
  // outside-click detection never fires — we do it ourselves. Clicks on the bell
  // are ignored here because the bell's onClick already toggles; handling both
  // would close and reopen to a net no-op.
  useEffect(() => {
    if (!open) return undefined;

    const onDocMouseDown = (event) => {
      if (anchorEl && anchorEl.contains(event.target)) return;
      if (event.target.closest?.('[data-ae-notification-panel]')) return;
      onClose();
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, anchorEl, onClose]);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      // OPUS-1527. Both of these are the point of the ticket rather than styling:
      //
      // 1. A stock Popover mounts a full-screen Modal root that swallows the
      //    first click outside it. That is a milder replay of the exact bug we
      //    are fixing — the operator taps a button, the tray closes, and the
      //    button does not fire. Hiding the backdrop and making the root
      //    transparent to pointer events lets that click reach what is beneath.
      //
      // 2. Drive is driven by F1-F12. The default Modal focus trap would take
      //    the keyboard the moment the tray opened and silently kill every
      //    shortcut mid-match, so all focus management is off.
      hideBackdrop
      disableAutoFocus
      disableEnforceFocus
      disableRestoreFocus
      disableScrollLock
      slotProps={{
        root: { sx: { pointerEvents: 'none' } },
        paper: {
          'data-ae-notification-panel': true,
          sx: {
            pointerEvents: 'auto',
            bgcolor: '#2A2A2A',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 2,
            boxShadow: '0 -8px 32px rgba(0,0,0,0.5)',
            minWidth: 320,
            maxWidth: 420,
          },
        },
      }}
    >
      <NotificationList />
    </Popover>
  );
};

NotificationPanel.propTypes = {
  open: PropTypes.bool,
  anchorEl: PropTypes.any,
  onClose: PropTypes.func.isRequired,
};

export default NotificationPanel;
